import { X, ExternalLink, Star, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const ServiceModal = ({ service, onClose }) => {
  if (!service) return null;

  const handleVisit = () => {
    window.open(service.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300">
        {/* Header */}
        <div className={`p-6 border-b ${service.color || 'bg-purple-50'}`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                {service.icon && <div className="text-3xl">{service.icon}</div>}
                <h2 className="text-2xl font-bold text-gray-900">{service.name}</h2>
              </div>
              {service.tagline && (
                <p className="text-gray-700 font-medium">{service.tagline}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="ml-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Description */}
          {service.description && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">About This Service</h3>
              <p className="text-gray-700">{service.description}</p>
            </div>
          )}

          {/* Benefits */}
          {service.benefits && service.benefits.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">What You Get</h3>
              <div className="space-y-2">
                {service.benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Price */}
          {service.price && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-700 font-medium">Estimated Cost:</span>
                <span className="text-2xl font-bold text-green-700">{service.price}</span>
              </div>
            </div>
          )}

          {/* Special Offer */}
          {service.specialOffer && (
            <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Star className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-orange-900">Special Offer</p>
                  <p className="text-orange-800">{service.specialOffer}</p>
                </div>
              </div>
            </div>
          )}

          {/* Why Recommended */}
          {service.whyRecommended && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Why We Recommend This</h4>
              <p className="text-blue-800">{service.whyRecommended}</p>
            </div>
          )}

          {/* Disclaimer */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
            <p className="mb-2">
              <strong>Note:</strong> By clicking "Visit Service", you'll be redirected to the provider's website in a new tab.
            </p>
            <p>
              Its-Sold.com partners with these services to help you save money. We may earn a commission at no extra cost to you.
            </p>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t bg-gray-50 flex items-center justify-between">
          <Button
            onClick={onClose}
            variant="outline"
            className="border-gray-300"
          >
            Close
          </Button>
          <Button
            onClick={handleVisit}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Visit Service
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ServiceModal;
